<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { RouterLink } from 'vue-router';
import { Check, Search, X } from '@lucide/vue';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { useHead } from '@unhead/vue';
import ScrollReveal from '@/components/ui/animation/ScrollReveal.vue';
import {
  CARD_SEO_DATA,
  MAJOR_ARCANA_SLUGS,
  MINOR_ARCANA_SLUGS,
  SUIT_LABELS,
  SUIT_ORDER,
  cardImagePath,
  cardImageSrc,
  type CardSeoData,
  type CardSuit,
} from '@app/data/cardSeoData';
import { cardOfDaySlug, todayKey } from '@app/data/kartyLinks';
import {
  APP_CTA_TO,
  APP_CTA_LABEL,
  TELEGRAM_CTA_LABEL,
  TELEGRAM_BOT_URL,
} from '@app/presentation/components/site/constants';
import SeoCardSticker from '@app/presentation/components/seo/SeoCardSticker.vue';
import SeoFinalCta from '@app/presentation/components/seo/SeoFinalCta.vue';

const majorCards: CardSeoData[] = MAJOR_ARCANA_SLUGS.map((s) => CARD_SEO_DATA[s]).filter(Boolean);
const minorCards: CardSeoData[] = MINOR_ARCANA_SLUGS.map((s) => CARD_SEO_DATA[s]).filter(Boolean);

const suitGroups: Array<{ suit: CardSuit; label: string; cards: CardSeoData[] }> = SUIT_ORDER.map(
  (suit) => ({
    suit,
    label: SUIT_LABELS[suit],
    cards: minorCards.filter((c) => c.suit === suit),
  }),
);

const orderedCards: CardSeoData[] = [...majorCards, ...minorCards];

// Deck order used for the absolute card-image URL (cards/<NN>.jpg). `orderedCards`
// is already in MAJOR+MINOR slug order, so its index IS the deck position.
const cardImageUrl = (i: number) => `https://tarotmeow.ru/cards/${String(i).padStart(2, '0')}.jpg`;

// ── Theme chips ──────────────────────────────────────────────────────────────
// Derive the theme set from every card's `themes`, ranked by frequency, and
// surface the most common few. Pure data → identical on SSR + client.
const THEME_CHIPS: string[] = (() => {
  const counts = new Map<string, number>();
  for (const c of orderedCards) for (const t of c.themes) counts.set(t, (counts.get(t) ?? 0) + 1);
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0], 'ru'))
    .slice(0, 8)
    .map(([t]) => t);
})();

// Seeded tilts for the cream tags / filter chips (index-based — identical on SSR + client).
const CHIP_TILTS = [-1.2, 0.9, -0.6, 1.2, -1];

// Quick hub links surfaced near the header / footer (tasteful, not spammy).
const HUB_LINKS: Array<{ to: string; label: string }> = [
  { to: '/karty/masti', label: 'По мастям' },
  { to: '/karty/da-ili-net', label: 'Да или Нет' },
  { to: '/karty/sochetaniya', label: 'Сочетания карт' },
  { to: '/karty/metodika', label: 'Как мы составляли значения' },
];

// ── Search + filters (client-only interactivity; default = "all visible") ─────
// All refs start at their no-filter state so the first client render matches the
// pre-rendered HTML; filtering is applied via per-card `visible` + v-show so every
// <RouterLink> stays in the DOM for crawlers.
const rawQuery = ref('');
const query = ref(''); // debounced
const activeArcana = ref<'major' | null>(null);
const activeSuit = ref<CardSuit | null>(null);
const activeTheme = ref<string | null>(null);
const activeYesNo = ref<'yes' | 'no' | 'maybe' | null>(null);

let debounceTimer: ReturnType<typeof setTimeout> | null = null;
watch(rawQuery, (v) => {
  if (debounceTimer) clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => {
    query.value = v.trim().toLowerCase();
  }, 180);
});

onUnmounted(() => {
  if (debounceTimer) clearTimeout(debounceTimer);
});

const anyFilterActive = computed(
  () =>
    query.value !== '' ||
    activeArcana.value !== null ||
    activeSuit.value !== null ||
    activeTheme.value !== null ||
    activeYesNo.value !== null,
);

const matches = (card: CardSeoData): boolean => {
  if (activeArcana.value && card.arcana !== activeArcana.value) return false;
  if (activeSuit.value && card.suit !== activeSuit.value) return false;
  if (activeTheme.value && !card.themes.includes(activeTheme.value)) return false;
  if (activeYesNo.value && card.correspondences.yesNo !== activeYesNo.value) return false;
  const q = query.value;
  if (q) {
    const haystack = [card.name, card.number, ...card.keywords].join(' ').toLowerCase();
    if (!haystack.includes(q)) return false;
  }
  return true;
};

// Per-slug visibility map. Recomputed reactively; v-show reads it without ever
// removing nodes from the DOM (SSR ships everything, client only toggles display).
const visibleMap = computed<Record<string, boolean>>(() => {
  const map: Record<string, boolean> = {};
  for (const c of orderedCards) map[c.slug] = matches(c);
  return map;
});

const visibleCount = computed(() => orderedCards.filter((c) => visibleMap.value[c.slug]).length);
const noResults = computed(() => anyFilterActive.value && visibleCount.value === 0);

// Per-section visible counts so we can hide a section's header when a filter empties it
// (v-show keeps the card links in the DOM for crawlers — never v-if).
const majorVisibleCount = computed(() => majorCards.filter((c) => visibleMap.value[c.slug]).length);
const suitVisibleCount = (suit: CardSuit): number =>
  minorCards.filter((c) => c.suit === suit && visibleMap.value[c.slug]).length;

const resetFilters = () => {
  rawQuery.value = '';
  query.value = '';
  activeArcana.value = null;
  activeSuit.value = null;
  activeTheme.value = null;
  activeYesNo.value = null;
};

const toggleArcana = () => {
  activeArcana.value = activeArcana.value === 'major' ? null : 'major';
};
const toggleSuit = (suit: CardSuit) => {
  activeSuit.value = activeSuit.value === suit ? null : suit;
};
const toggleTheme = (theme: string) => {
  activeTheme.value = activeTheme.value === theme ? null : theme;
};
const toggleYesNo = (val: 'yes' | 'no') => {
  activeYesNo.value = activeYesNo.value === val ? null : val;
};

// ── Card of the day (CLIENT-SIDE ONLY) ────────────────────────────────────────
// Starts null so SSR + first client paint render the neutral skeleton — never a
// build-time card (that would freeze to the build date and break hydration). The
// real pick is computed in onMounted from the *visitor's* local date.
const cardOfDay = ref<string | null>(null);
const cardOfDayData = computed(() => (cardOfDay.value ? CARD_SEO_DATA[cardOfDay.value] : null));

// First sentence of the tldr for a compact teaser.
const firstSentence = (text: string): string => {
  const m = text.match(/^[^.!?]*[.!?]/);
  return (m ? m[0] : text).trim();
};

onMounted(() => {
  cardOfDay.value = cardOfDaySlug(todayKey(new Date()), [
    ...MAJOR_ARCANA_SLUGS,
    ...MINOR_ARCANA_SLUGS,
  ]);
});

useHead({
  title: 'Значение Карт Таро — Все 78 Карт Колоды | Таро Мяу',
  meta: [
    {
      name: 'description',
      content:
        'Значение всех 78 карт Таро: старшие и младшие арканы, прямое и перевёрнутое положение, любовь, работа, совет. Толкования с персональной интерпретацией от Таро Мяу.',
    },
    {
      name: 'keywords',
      content:
        'значение карт таро, карты таро значение, толкование карт таро, старшие арканы, младшие арканы, таро значение карт колоды',
    },
    { property: 'og:title', content: 'Значение Карт Таро — Все 78 Карт Колоды | Таро Мяу' },
    {
      property: 'og:description',
      content: 'Толкование всех 78 карт Таро: значение в прямом и перевёрнутом положении с персональной интерпретацией.',
    },
    { property: 'og:image', content: 'https://tarotmeow.ru/og-image.jpg' },
    { property: 'og:locale', content: 'ru_RU' },
    { property: 'og:type', content: 'website' },
    { property: 'og:site_name', content: 'Таро Мяу' },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: 'Значение Карт Таро — Все 78 Карт Колоды | Таро Мяу' },
    {
      name: 'twitter:description',
      content: 'Толкование всех 78 карт Таро: значение в прямом и перевёрнутом положении с персональной интерпретацией.',
    },
    { name: 'twitter:image', content: 'https://tarotmeow.ru/og-image.jpg' },
  ],
  link: [
    { rel: 'canonical', href: 'https://tarotmeow.ru/karty' },
    { rel: 'alternate', hreflang: 'ru', href: 'https://tarotmeow.ru/karty' },
    { rel: 'alternate', hreflang: 'x-default', href: 'https://tarotmeow.ru/karty' },
  ],
  script: [
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Главная', item: 'https://tarotmeow.ru/' },
          { '@type': 'ListItem', position: 2, name: 'Карты', item: 'https://tarotmeow.ru/karty' },
        ],
      }),
    },
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: 'Значение карт Таро — все 78 карт колоды',
        description:
          'Толкование всех 78 карт Таро: старшие и младшие арканы, прямое и перевёрнутое значение, любовь, работа, совет.',
        url: 'https://tarotmeow.ru/karty',
        inLanguage: 'ru-RU',
        isPartOf: { '@type': 'WebSite', name: 'Таро Мяу', alternateName: 'Tarot Meow', url: 'https://tarotmeow.ru/' },
        mainEntity: {
          '@type': 'ItemList',
          name: 'Карты Таро — значения 78 карт',
          numberOfItems: orderedCards.length,
          itemListElement: orderedCards.map((c, i) => ({
            '@type': 'ListItem',
            position: i + 1,
            name: c.name,
            url: `https://tarotmeow.ru/karty/${c.slug}`,
            image: cardImageUrl(i),
          })),
        },
      }),
    },
  ],
});
</script>

<template>
  <div class="py-16 md:py-24">
    <div class="site-container">
      <!-- Header -->
      <header class="mb-10 text-center md:mb-12">
        <div class="type-overline mb-3.5 text-primary-ink">Полная колода</div>
        <h1 class="type-display">Значение карт Таро</h1>
        <p class="type-body mx-auto mt-3.5 max-w-2xl text-muted-foreground md:text-[17px]">
          Все 78 карт колоды — 22 старших и 56 младших арканов. Выбери карту, чтобы узнать её
          значение в прямом и перевёрнутом положении, в любви, работе и совете.
        </p>
        <nav class="mt-5 flex flex-wrap items-center justify-center gap-x-2.5 gap-y-3">
          <RouterLink
            v-for="(hub, i) in HUB_LINKS"
            :key="hub.to"
            :to="hub.to"
            class="seo-tag seo-press paper-tag type-label px-3 py-1"
            :style="{ '--tilt': `${CHIP_TILTS[i % CHIP_TILTS.length]}deg` }"
          >
            {{ hub.label }}
          </RouterLink>
        </nav>
      </header>

      <!-- Карта дня (client-only: ничего не пекём в HTML) -->
      <section
        aria-label="Карта дня"
        class="paper-sheet mb-14 flex flex-col gap-5 p-5 sm:flex-row sm:items-center sm:gap-7 sm:p-6 md:mb-16"
      >
        <!-- Image / skeleton -->
        <RouterLink
          v-if="cardOfDayData"
          :to="`/karty/${cardOfDayData.slug}`"
          class="seo-press ml-1 block w-24 shrink-0 sm:w-28"
        >
          <span
            class="seo-sticker"
            style="--tilt: -2deg"
          >
            <picture class="contents">
              <source
                type="image/avif"
                :srcset="cardImageSrc(cardOfDayData.slug).avif"
              >
              <source
                type="image/webp"
                :srcset="cardImageSrc(cardOfDayData.slug).webp"
              >
              <img
                :src="cardImagePath(cardOfDayData.slug)"
                :alt="`Карта Таро ${cardOfDayData.name}`"
                width="275"
                height="483"
                loading="lazy"
                decoding="async"
                class="aspect-[275/483] w-full object-cover"
              >
            </picture>
          </span>
        </RouterLink>
        <Skeleton
          v-else
          class="ml-1 aspect-[275/483] w-24 shrink-0 rotate-[-2deg] sm:w-28"
          aria-hidden="true"
        />

        <!-- Copy -->
        <div class="min-w-0 flex-1">
          <span
            class="seo-tag paper-tag type-overline px-2.5 py-0.5"
            style="--tilt: -1.5deg"
          >Карта дня</span>
          <template v-if="cardOfDayData">
            <h2 class="type-heading mt-3">
              {{ cardOfDayData.name }}
            </h2>
            <p class="type-body mt-2 max-w-xl text-muted-foreground">
              {{ firstSentence(cardOfDayData.tldr) }}
            </p>
            <div class="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-5">
              <Button
                as-child
                size="sm"
                class="self-start"
              >
                <RouterLink :to="`/karty/${cardOfDayData.slug}`">
                  Открыть карту дня
                </RouterLink>
              </Button>
              <RouterLink
                to="/rasklady/karta-dnya"
                class="seo-more type-label"
              >
                А что выпадет тебе? →
              </RouterLink>
            </div>
          </template>
          <template v-else>
            <Skeleton
              class="mt-3 h-7 w-40"
              aria-hidden="true"
            />
            <Skeleton
              class="mt-3 h-4 w-full max-w-md"
              aria-hidden="true"
            />
            <Skeleton
              class="mt-2 h-4 w-2/3"
              aria-hidden="true"
            />
          </template>
        </div>
      </section>

      <!-- Поиск + фильтры (клиентская интерактивность; по умолчанию всё видно) -->
      <div class="mb-12 md:mb-14">
        <div class="paper-sheet p-4 sm:p-5">
          <!-- Search: a recessed paper well -->
          <div class="relative">
            <label
              for="card-search"
              class="sr-only"
            >Поиск по картам Таро</label>
            <Search
              class="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden="true"
            />
            <Input
              id="card-search"
              v-model="rawQuery"
              type="search"
              inputmode="search"
              autocomplete="off"
              placeholder="Найти карту по названию или смыслу…"
              class="k-search pl-10"
            />
          </div>

          <!-- Chips: арканы / масти -->
          <div class="mt-4 flex flex-wrap gap-x-2 gap-y-2.5">
            <button
              type="button"
              :aria-pressed="activeArcana === 'major'"
              class="k-chip paper-tag type-label inline-flex items-center gap-1 px-3 py-1.5"
              :class="{ 'is-on': activeArcana === 'major' }"
              :style="{ '--tilt': `${CHIP_TILTS[0]}deg` }"
              @click="toggleArcana"
            >
              <Check
                v-if="activeArcana === 'major'"
                class="-ml-0.5 h-3.5 w-3.5"
                :stroke-width="3"
                aria-hidden="true"
              />
              Старшие арканы
            </button>
            <button
              v-for="(suit, i) in SUIT_ORDER"
              :key="suit"
              type="button"
              :aria-pressed="activeSuit === suit"
              class="k-chip paper-tag type-label inline-flex items-center gap-1 px-3 py-1.5"
              :class="{ 'is-on': activeSuit === suit }"
              :style="{ '--tilt': `${CHIP_TILTS[(i + 1) % CHIP_TILTS.length]}deg` }"
              @click="toggleSuit(suit)"
            >
              <Check
                v-if="activeSuit === suit"
                class="-ml-0.5 h-3.5 w-3.5"
                :stroke-width="3"
                aria-hidden="true"
              />
              {{ SUIT_LABELS[suit] }}
            </button>
          </div>

          <!-- Chips: темы — quieter pencil-outlined slips -->
          <div class="mt-3 flex flex-wrap gap-2">
            <button
              v-for="theme in THEME_CHIPS"
              :key="theme"
              type="button"
              :aria-pressed="activeTheme === theme"
              class="k-theme type-label inline-flex items-center gap-1 px-3 py-1 capitalize"
              :class="{ 'is-on paper-tag': activeTheme === theme }"
              @click="toggleTheme(theme)"
            >
              <Check
                v-if="activeTheme === theme"
                class="-ml-0.5 h-3.5 w-3.5"
                :stroke-width="3"
                aria-hidden="true"
              />
              {{ theme }}
            </button>
          </div>

          <!-- Chips: Да / Нет + счётчик + сброс -->
          <div class="k-rule mt-4 flex flex-wrap items-center justify-between gap-3 pt-4">
            <div
              class="flex items-center gap-2"
              role="group"
              aria-label="Ответ «да или нет»"
            >
              <span class="type-caption">Ответ:</span>
              <button
                type="button"
                :aria-pressed="activeYesNo === 'yes'"
                class="k-chip paper-tag type-label inline-flex items-center gap-1 px-3 py-1"
                :class="{ 'is-on is-yes': activeYesNo === 'yes' }"
                style="--tilt: -1deg"
                @click="toggleYesNo('yes')"
              >
                <Check
                  v-if="activeYesNo === 'yes'"
                  class="-ml-0.5 h-3.5 w-3.5"
                  :stroke-width="3"
                  aria-hidden="true"
                />
                Да
              </button>
              <button
                type="button"
                :aria-pressed="activeYesNo === 'no'"
                class="k-chip paper-tag type-label inline-flex items-center gap-1 px-3 py-1"
                :class="{ 'is-on is-no': activeYesNo === 'no' }"
                style="--tilt: 1.2deg"
                @click="toggleYesNo('no')"
              >
                <Check
                  v-if="activeYesNo === 'no'"
                  class="-ml-0.5 h-3.5 w-3.5"
                  :stroke-width="3"
                  aria-hidden="true"
                />
                Нет
              </button>
            </div>

            <div class="flex items-center gap-3">
              <span
                class="type-caption"
                aria-live="polite"
              >
                Показано {{ visibleCount }} из {{ orderedCards.length }}
              </span>
              <button
                v-if="anyFilterActive"
                type="button"
                class="type-label inline-flex min-h-8 items-center gap-1 rounded-[var(--paper-radius-xs)] px-2 text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-paper-stock"
                @click="resetFilters"
              >
                <X
                  class="h-3.5 w-3.5"
                  aria-hidden="true"
                /> Сбросить
              </button>
            </div>
          </div>
        </div>

        <!-- Пустой результат -->
        <p
          v-show="noResults"
          role="status"
          aria-live="polite"
          class="paper-sheet type-body mt-6 px-5 py-8 text-center text-muted-foreground"
        >
          Мяу, по этому запросу карт не нашлось. Попробуй другое слово или
          <button
            type="button"
            class="seo-link"
            @click="resetFilters"
          >
            сбрось фильтры
          </button>.
        </p>
      </div>

      <!-- Старшие арканы -->
      <section
        v-show="!anyFilterActive || majorVisibleCount > 0"
        class="mb-16 md:mb-24"
      >
        <div class="mb-8 flex items-center gap-3">
          <span
            class="seo-tag paper-tag type-overline shrink-0 px-2.5 py-0.5"
            style="--tilt: -2deg"
          >
            22 карты
          </span>
          <h2 class="type-title">Старшие арканы</h2>
        </div>

        <ul class="grid grid-cols-3 gap-x-4 gap-y-6 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 lg:gap-x-6 lg:gap-y-8">
          <li
            v-for="(card, i) in majorCards"
            v-show="visibleMap[card.slug]"
            :key="card.slug"
          >
            <SeoCardSticker
              :slug="card.slug"
              :name="card.name"
              :number="card.number"
              :index="i"
              :priority="i < 6"
            />
          </li>
        </ul>
      </section>

      <!-- Младшие арканы по мастям -->
      <section
        v-for="(group, gi) in suitGroups"
        v-show="!anyFilterActive || suitVisibleCount(group.suit) > 0"
        :key="group.suit"
        class="mb-16 last:mb-0 md:mb-24"
      >
        <div class="mb-8 flex items-center gap-3">
          <span
            class="seo-tag paper-tag type-overline shrink-0 px-2.5 py-0.5"
            :style="{ '--tilt': `${CHIP_TILTS[gi % CHIP_TILTS.length]}deg` }"
          >
            14 карт
          </span>
          <h2 class="type-title">{{ group.label }}</h2>
        </div>

        <ul class="grid grid-cols-3 gap-x-4 gap-y-6 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-7 lg:gap-x-6 lg:gap-y-8">
          <li
            v-for="(card, i) in group.cards"
            v-show="visibleMap[card.slug]"
            :key="card.slug"
          >
            <SeoCardSticker
              :slug="card.slug"
              :name="card.name"
              :number="card.number"
              :index="i + gi"
            />
          </li>
        </ul>
      </section>

      <!-- CTA -->
      <ScrollReveal class="mt-16 md:mt-24">
        <SeoFinalCta cat>
          <h2 class="type-title">Узнай свою карту в раскладе</h2>
          <p class="type-body mx-auto mt-3.5 max-w-xl text-muted-foreground md:text-[17px]">
            Теория — это хорошо, но живой расклад точнее. Вытяни карту и получи персональную
            интерпретацию. Первый расклад бесплатно.
          </p>
          <div class="mt-7 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Button
              as-child
              size="lg"
            >
              <RouterLink :to="APP_CTA_TO">{{ APP_CTA_LABEL }}</RouterLink>
            </Button>
            <Button
              as-child
              variant="outline"
              size="lg"
            >
              <a
                :href="TELEGRAM_BOT_URL"
                target="_blank"
                rel="noopener"
              >{{ TELEGRAM_CTA_LABEL }}</a>
            </Button>
            <Button
              as-child
              variant="ghost"
              size="lg"
            >
              <RouterLink to="/rasklady">Все расклады</RouterLink>
            </Button>
          </div>
          <nav class="mt-6 flex flex-wrap items-center justify-center gap-x-2 gap-y-1">
            <template
              v-for="(hub, i) in HUB_LINKS"
              :key="hub.to"
            >
              <span
                v-if="i > 0"
                class="text-muted-foreground/60"
                aria-hidden="true"
              >·</span>
              <RouterLink
                :to="hub.to"
                class="seo-more type-caption px-1 hover:text-foreground"
              >
                {{ hub.label }}
              </RouterLink>
            </template>
          </nav>
        </SeoFinalCta>
      </ScrollReveal>
    </div>
  </div>
</template>

<style scoped src="../components/seo/seo-paper.css"></style>

<style scoped>
/* The browser's own (blue) clear button is replaced by the reset. */
.k-search::-webkit-search-cancel-button {
  appearance: none;
}

/* Filter tags — the Codex filter look: cream, a little askew; the active one
   straightens, lifts and gets a tick (+ an ink ring). Pressing pushes them flat. */
.k-chip {
  min-height: 34px;
  border-radius: var(--paper-radius-xs);
  rotate: var(--tilt, 0deg);
  box-shadow: inset 0 1px 0 rgb(255 255 255 / 0.5), 0 2px 0 rgb(4 3 18 / 0.35), 0 3px 6px -2px var(--paper-shade);
  transition:
    rotate 0.2s cubic-bezier(0.22, 1, 0.36, 1),
    translate 0.2s cubic-bezier(0.22, 1, 0.36, 1),
    box-shadow 0.2s ease-out;
}
.k-chip:active {
  translate: 0 1px;
}
.k-chip.is-on {
  rotate: 0deg;
  translate: 0 -2px;
  box-shadow:
    inset 0 1px 0 rgb(255 255 255 / 0.55),
    0 0 0 2px var(--primary-ink),
    0 8px 14px -6px var(--paper-shade);
}
.k-chip.is-yes {
  box-shadow:
    inset 0 1px 0 rgb(255 255 255 / 0.55),
    0 0 0 2px var(--success),
    0 8px 14px -6px var(--paper-shade);
}
.k-chip.is-no {
  box-shadow:
    inset 0 1px 0 rgb(255 255 255 / 0.55),
    0 0 0 2px var(--destructive),
    0 8px 14px -6px var(--paper-shade);
}

/* Theme slips: pencil-outlined on the sheet until picked, then cream. */
.k-theme {
  min-height: 30px;
  border-radius: var(--paper-radius-xs);
  border: 1px dashed rgb(242 235 221 / 0.3);
  color: var(--muted-foreground);
  transition: color 0.15s ease-out;
}
.k-theme:hover {
  color: var(--foreground);
}
.k-theme.is-on {
  border-color: transparent;
  color: var(--paper-stock-ink);
  box-shadow: inset 0 1px 0 rgb(255 255 255 / 0.5), 0 3px 6px -2px var(--paper-shade);
}
.k-chip:focus-visible,
.k-theme:focus-visible {
  outline: 2px solid var(--paper-stock);
  outline-offset: 3px;
}
.k-rule {
  border-top: 1px dashed rgb(242 235 221 / 0.14);
}
@media (prefers-reduced-motion: reduce) {
  .k-chip,
  .k-theme {
    transition: none;
  }
}
</style>
